import React, { useEffect, useState } from "react";
import { useNavigate } from 'react-router-dom';

import api from "../../api/axios";
import {
    Crown,
    Rocket,
    Building2,
    Users,
    Film,
    LayoutTemplate,
    Megaphone,
    Sparkles,
    Loader2,
    Star,
    CreditCard,
    Lock,
    CheckCircle,
    X,
    ArrowRight,
} from "lucide-react";
import Footer from "../../layouts/Footer";
import { getUser } from "../../api/auth";
import AuthPopup from "../../components/popup/AuthPopup";

export default function SubscriptionPlansComponent({ role }) {
    const [plans, setPlans] = useState([]);
    const [loading, setLoading] = useState(true);
    const [billingCycle, setBillingCycle] = useState("YEARLY");
    const [currentPlan, setCurrentPlan] = useState("FREE"); // Replace with real data

    // Modal states
    const [showModal, setShowModal] = useState(false);
    const [selectedPlan, setSelectedPlan] = useState(null);

    // Payment form states
    const [paymentMethod, setPaymentMethod] = useState("card");
    const [cardNumber, setCardNumber] = useState("");
    const [expiry, setExpiry] = useState("");
    const [cvv, setCvv] = useState("");
    const [upiId, setUpiId] = useState("");
    const [bank, setBank] = useState("");
    const [loadingPayment, setLoadingPayment] = useState(false);
    const [paymentSuccess, setPaymentSuccess] = useState(false);
    const [paymentError, setPaymentError] = useState("");
    const [showAuth, setShowAuth] = useState(false);

    const navigate = useNavigate();
    const user = getUser();


    useEffect(() => {
        loadPlans();
    }, []);

    const loadPlans = async () => {
        try {
            setLoading(true);
            const response = await api.get("/subscription/plans");
            if (response?.data?.success) {
                setPlans(response.data.data || []);
            } else {
                setPlans([]);
            }
        } catch (err) {
            console.error(err);
            setPlans([]);
        } finally {
            setLoading(false);
        }
    };

    const getPlanStyles = (planKey) => {
        switch (planKey) {
            case "FREE":
                return {
                    border: "border-gray-200",
                    bg: "bg-white",
                    text: "text-gray-900",
                    iconBg: "bg-gray-100 text-gray-600",
                    button: "bg-gray-800 hover:bg-gray-900",
                };
            case "STARTER":
                return {
                    border: "border-gray-200",
                    bg: "bg-white",
                    text: "text-gray-900",
                    iconBg: "bg-blue-100 text-blue-600",
                    button: "bg-blue-600 hover:bg-blue-700",
                };
            case "PRO":
                return {
                    border: "border-gray-200",
                    bg: "bg-gradient-to-br from-indigo-600 via-purple-600 to-pink-600",
                    text: "text-white",
                    iconBg: "bg-white/20 text-white",
                    button: "bg-white text-indigo-700 hover:bg-indigo-50",
                    isPopular: true,
                };
            case "ENTERPRISE":
                return {
                    border: "border-gray-200",
                    bg: "bg-gradient-to-br from-emerald-600 to-teal-600",
                    text: "text-white",
                    iconBg: "bg-white/20 text-white",
                    button: "bg-white text-emerald-700 hover:bg-emerald-50",
                };
            default:
                return {
                    border: "border-gray-200",
                    bg: "bg-white",
                    text: "text-gray-900",
                    iconBg: "bg-gray-100 text-gray-600",
                    button: "bg-gray-800 hover:bg-gray-900",
                };
        }
    };

    const getPlanIcon = (planKey) => {
        switch (planKey) {
            case "FREE":
                return Crown;
            case "STARTER":
                return Star;
            case "PRO":
                return Rocket;
            case "ENTERPRISE":
                return Building2;
            default:
                return Crown;
        }
    };

    const formatLimit = (value) => {
        return value >= 999999 ? "♾️ Unlimited" : value;
    };

    const openModal = (plan) => {
        if (!user) {
            setShowAuth(true);
            return;
        }
        setSelectedPlan(plan);
        setShowModal(true);
        setPaymentSuccess(false);
        setPaymentError("");
        // Reset form fields
        setCardNumber("");
        setExpiry("");
        setCvv("");
        setUpiId("");
        setBank("");
    };

    const closeModal = () => {
        setShowModal(false);
        setSelectedPlan(null);
    };

    const handlePayment = async (e) => {
        e.preventDefault();
        setPaymentError("");
        setLoadingPayment(true);

        try {
            // Simulate API call – replace with real payment integration
            await new Promise((resolve) => setTimeout(resolve, 2000));
            setPaymentSuccess(true);
            // Optionally update current plan here after success
            // setCurrentPlan(selectedPlan.plan_key);
        } catch (err) {
            setPaymentError("Payment failed. Please try again.");
        } finally {
            setLoadingPayment(false);
        }
    };

    if (loading) {
        return (
            <div className="flex h-[70vh] items-center justify-center">
                <Loader2 className="h-12 w-12 animate-spin text-indigo-600" />
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
            {/* Hero Section – no external image */}
            <section className="relative overflow-hidden bg-gradient-to-r from-slate-900 via-indigo-900 to-slate-900 text-white">
                <div className="relative mx-auto max-w-7xl px-6 py-24 text-center">
                    <div className="mx-auto max-w-3xl">
                        <div className="mb-6 inline-flex items-center gap-2 rounded-full bg-white/10 px-4 py-2 text-sm font-medium backdrop-blur-sm">
                            <Sparkles size={18} />
                            Flexible Pricing for Every Team
                        </div>
                        <h1 className="text-5xl font-extrabold tracking-tight sm:text-6xl">
                            Choose Your <span className="text-indigo-300">Perfect</span> Plan
                        </h1>
                        <p className="mt-6 text-lg text-slate-300">
                            Simple, transparent pricing that scales with your business.
                            Upgrade anytime – no hidden fees.
                        </p>
                    </div>
                </div>
            </section>

            {/* Plans Grid */}
            <div className="mx-auto max-w-7xl px-6 py-12">
                {plans.length === 0 ? (
                    <div className="flex flex-col items-center justify-center rounded-3xl border border-dashed border-gray-300 bg-white/60 py-24 backdrop-blur-sm">
                        <Crown size={70} className="mb-5 text-gray-300" />
                        <h2 className="text-2xl font-semibold text-gray-700">
                            No Plans Available
                        </h2>
                        <p className="mt-2 max-w-md text-center text-gray-500">
                            We're currently updating our subscription plans. Please check back
                            later or contact support.
                        </p>
                    </div>
                ) : (
                    <div className="grid gap-8 md:grid-cols-2 xl:grid-cols-4">
                        {plans.map((plan) => {
                            const styles = getPlanStyles(plan.plan_key);
                            const Icon = getPlanIcon(plan.plan_key);
                            const isCurrent = currentPlan === plan.plan_key;
                            const isPopular = styles.isPopular || false;

                            return (
                                <div
                                    key={plan.plan_id}
                                    className={`group relative flex flex-col overflow-hidden rounded-3xl border ${styles.border} bg-white/80 backdrop-blur-sm shadow-sm transition-all duration-300 hover:-translate-y-2 hover:shadow-2xl hover:shadow-indigo-100/50`}
                                >
                                    {/* Popular Badge */}
                                    {isPopular && (
                                        <div className="absolute -right-12 top-10 rotate-45 bg-yellow-400 px-12 py-1 text-xs font-bold uppercase text-yellow-900 shadow-lg">
                                            Popular
                                        </div>
                                    )}

                                    {/* Current Plan Badge */}
                                    {isCurrent && (
                                        <div className="absolute right-5 top-5 rounded-full bg-green-500 px-3 py-1 text-xs font-semibold text-white shadow-md">
                                            Current Plan
                                        </div>
                                    )}

                                    {/* Plan Header */}
                                    <div
                                        className={`p-6 ${styles.bg} ${styles.text} transition-colors duration-300`}
                                    >
                                        <div
                                            className={`mb-4 inline-flex h-14 w-14 items-center justify-center rounded-2xl ${styles.iconBg} transition-transform group-hover:scale-110`}
                                        >
                                            <Icon size={28} />
                                        </div>
                                        <h3 className="text-2xl font-bold">{plan.plan_name}</h3>
                                        <p className="mt-1 text-sm opacity-80">
                                            {plan.plan_description}
                                        </p>
                                        <div className="mt-6 flex items-end">
                                            <span className="text-5xl font-extrabold tracking-tight">
                                                ₹{plan.plan_price}
                                            </span>
                                            {/* <span className="ml-2 text-sm opacity-70">
                                                /{billingCycle.toLowerCase()}
                                            </span> */}
                                        </div>
                                    </div>

                                    {/* Features */}
                                    <div className="flex-1 space-y-3 p-6">
                                        {[
                                            { icon: Users, label: "Users", value: plan.plan_limits.users },
                                            { icon: Building2, label: "Brands", value: plan.plan_limits.brands },
                                            // {
                                            //     icon: LayoutTemplate,
                                            //     label: "Templates",
                                            //     value: plan.plan_limits.templates,
                                            // },
                                            { icon: Film, label: "Creatives", value: plan.plan_limits.creatives },
                                            {
                                                icon: Megaphone,
                                                label: "Campaigns",
                                                value: plan.plan_limits.campaigns,
                                            },
                                        ].map((item, idx) => (
                                            <div
                                                key={idx}
                                                className="flex items-center justify-between border-b border-gray-100 pb-2 last:border-0 last:pb-0"
                                            >
                                                <div className="flex items-center gap-3">
                                                    <div className="text-indigo-500">
                                                        <item.icon size={18} />
                                                    </div>
                                                    <span className="text-sm font-medium text-gray-700">
                                                        {item.label}
                                                    </span>
                                                </div>
                                                <span className="text-sm font-bold text-gray-900">
                                                    {formatLimit(item.value)}
                                                </span>
                                            </div>
                                        ))}
                                    </div>

                                    {/* Action Button */}
                                    <div className="border-t border-gray-100 p-6">
                                        <button
                                            className={`w-full rounded-xl py-3 font-semibold transition-all duration-200 ${isCurrent
                                                ? "cursor-default opacity-40 bg-green-600 text-white"
                                                : isPopular
                                                    ? "bg-indigo-600 text-white hover:bg-indigo-700 hover:shadow-lg"
                                                    : "bg-slate-800 text-white hover:bg-slate-900 hover:shadow-lg"
                                                }`}
                                            disabled={isCurrent}
                                            onClick={() => !isCurrent && openModal(plan)}
                                        >
                                            {isCurrent ? "Current Plan" : "Upgrade Now →"}
                                        </button>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>

            {/* Bottom CTA */}
            {plans.length > 0 && (
                <section className="border-t bg-white/60 backdrop-blur-sm">
                    <div className="mx-auto flex max-w-7xl flex-col items-center px-6 py-20 text-center">
                        <div className="rounded-full bg-indigo-100 p-4 text-indigo-600">
                            <Sparkles size={40} />
                        </div>
                        <h2 className="mt-6 text-4xl font-bold text-gray-900">
                            Need a custom plan?
                        </h2>
                        <p className="mt-4 max-w-2xl text-lg text-gray-600">
                            Get a tailored subscription with dedicated support, custom
                            integrations, priority onboarding, and enterprise-grade security.
                        </p>
                        <button
                            className="mt-10 rounded-xl bg-slate-900 px-8 py-4 font-semibold text-white shadow-lg transition hover:bg-slate-800 hover:shadow-xl"
                            onClick={() => navigate("/contact")}
                        >
                            Contact Sales
                        </button>
                    </div>
                </section>
            )}


            {/* ============ PAYMENT POPUP MODAL ============ */}
            {showModal && selectedPlan && (
                <div
                    className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm transition-all"
                    onClick={closeModal} // click outside to close
                >
                    <div
                        className="relative mx-4 w-full max-w-3xl max-h-[90vh] overflow-y-auto rounded-3xl bg-white shadow-2xl"
                        onClick={(e) => e.stopPropagation()} // prevent closing when clicking inside
                    >
                        {/* Close button */}
                        <button
                            onClick={closeModal}
                            className="absolute right-4 top-4 rounded-full bg-gray-100 p-2 text-gray-600 hover:bg-gray-200"
                        >
                            <X size={20} />
                        </button>

                        {paymentSuccess ? (
                            // Success view
                            <div className="p-8 text-center">
                                <div className="mx-auto mb-4 flex h-20 w-20 items-center justify-center rounded-full bg-green-100">
                                    <CheckCircle className="h-12 w-12 text-green-600" />
                                </div>
                                <h2 className="text-2xl font-bold text-gray-900">
                                    Payment Successful!
                                </h2>
                                <p className="mt-2 text-gray-600">
                                    Your subscription to{" "}
                                    <strong>{selectedPlan.plan_name}</strong> is now active.
                                </p>
                                <button
                                    onClick={() => {
                                        closeModal();
                                        // Optionally update current plan
                                        // setCurrentPlan(selectedPlan.plan_key);
                                    }}
                                    className="mt-6 rounded-xl bg-indigo-600 px-6 py-3 font-semibold text-white hover:bg-indigo-700"
                                >
                                    Done
                                </button>
                            </div>
                        ) : (
                            // Payment form
                            <div className="grid gap-8 p-6 md:grid-cols-5">
                                {/* Left: Plan Summary */}
                                <div className="md:col-span-2">
                                    <h3 className="text-lg font-semibold">Selected Plan</h3>
                                    <div className="mt-4">
                                        <p className="text-2xl font-bold">{selectedPlan.plan_name}</p>
                                        <p className="text-sm text-gray-500">
                                            {selectedPlan.plan_description}
                                        </p>
                                        <p className="mt-2 text-3xl font-extrabold text-indigo-600">
                                            ₹{selectedPlan.plan_price}
                                            {/* <span className="text-sm font-normal text-gray-500">
                                                /{selectedPlan.plan_billing_cycle?.toLowerCase()}
                                            </span> */}
                                        </p>
                                    </div>
                                    <hr className="my-4" />
                                    <ul className="space-y-2 text-sm">
                                        {Object.entries(selectedPlan.plan_limits).map(([key, value]) => (
                                            <li key={key} className="flex justify-between">
                                                <span className="capitalize text-gray-600">{key}</span>
                                                <span className="font-medium">
                                                    {value >= 999999 ? "Unlimited" : value}
                                                </span>
                                            </li>
                                        ))}
                                    </ul>
                                </div>

                                {/* Right: Payment Form */}
                                <div className="md:col-span-3">
                                    <h3 className="text-lg font-semibold">Payment Details</h3>

                                    {/* Payment Method Selector */}
                                    <div className="mt-4 grid grid-cols-3 gap-2">
                                        {["card", "upi", "netbanking"].map((method) => (
                                            <button
                                                key={method}
                                                className={`rounded-lg border p-3 text-sm font-medium capitalize transition ${paymentMethod === method
                                                    ? "border-indigo-600 bg-indigo-50 text-indigo-700"
                                                    : "border-gray-200 hover:bg-gray-50"
                                                    }`}
                                                onClick={() => setPaymentMethod(method)}
                                            >
                                                {method === "card" && <CreditCard className="inline mr-1" size={16} />}
                                                {method}
                                            </button>
                                        ))}
                                    </div>

                                    <form onSubmit={handlePayment} className="mt-6 space-y-4">
                                        {paymentMethod === "card" && (
                                            <>
                                                <div>
                                                    <label className="block text-sm font-medium text-gray-700">
                                                        Card Number
                                                    </label>
                                                    <input
                                                        type="text"
                                                        placeholder="1234 5678 9012 3456"
                                                        value={cardNumber}
                                                        onChange={(e) => setCardNumber(e.target.value)}
                                                        className="mt-1 w-full rounded-lg border border-gray-300 px-4 py-2 focus:border-indigo-500 focus:outline-none"
                                                        required
                                                    />
                                                </div>
                                                <div className="grid grid-cols-2 gap-4">
                                                    <div>
                                                        <label className="block text-sm font-medium text-gray-700">
                                                            Expiry
                                                        </label>
                                                        <input
                                                            type="text"
                                                            placeholder="MM/YY"
                                                            value={expiry}
                                                            onChange={(e) => setExpiry(e.target.value)}
                                                            className="mt-1 w-full rounded-lg border border-gray-300 px-4 py-2 focus:border-indigo-500 focus:outline-none"
                                                            required
                                                        />
                                                    </div>
                                                    <div>
                                                        <label className="block text-sm font-medium text-gray-700">
                                                            CVV
                                                        </label>
                                                        <input
                                                            type="password"
                                                            placeholder="•••"
                                                            value={cvv}
                                                            onChange={(e) => setCvv(e.target.value)}
                                                            className="mt-1 w-full rounded-lg border border-gray-300 px-4 py-2 focus:border-indigo-500 focus:outline-none"
                                                            required
                                                        />
                                                    </div>
                                                </div>
                                            </>
                                        )}

                                        {paymentMethod === "upi" && (
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700">
                                                    UPI ID
                                                </label>
                                                <input
                                                    type="text"
                                                    placeholder="example@upi"
                                                    value={upiId}
                                                    onChange={(e) => setUpiId(e.target.value)}
                                                    className="mt-1 w-full rounded-lg border border-gray-300 px-4 py-2 focus:border-indigo-500 focus:outline-none"
                                                    required
                                                />
                                            </div>
                                        )}

                                        {paymentMethod === "netbanking" && (
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700">
                                                    Select Bank
                                                </label>
                                                <select
                                                    value={bank}
                                                    onChange={(e) => setBank(e.target.value)}
                                                    className="mt-1 w-full rounded-lg border border-gray-300 px-4 py-2 focus:border-indigo-500 focus:outline-none"
                                                    required
                                                >
                                                    <option value="">Choose your bank</option>
                                                    <option value="sbi">State Bank of India</option>
                                                    <option value="hdfc">HDFC Bank</option>
                                                    <option value="icici">ICICI Bank</option>
                                                    <option value="axis">Axis Bank</option>
                                                </select>
                                            </div>
                                        )}

                                        {paymentError && (
                                            <div className="rounded-lg bg-red-50 p-3 text-sm text-red-600">
                                                {paymentError}
                                            </div>
                                        )}

                                        <div className="flex items-center justify-between pt-4">
                                            <div className="flex items-center gap-2 text-sm text-gray-500">
                                                <Lock size={16} />
                                                <span>Secure payment</span>
                                            </div>
                                            <button
                                                type="submit"
                                                disabled={loadingPayment}
                                                className="rounded-xl bg-indigo-600 px-8 py-3 font-semibold text-white shadow-md transition hover:bg-indigo-700 disabled:opacity-70"
                                            >
                                                {loadingPayment ? (
                                                    <span className="flex items-center gap-2">
                                                        <Loader2 className="animate-spin" size={18} />
                                                        Processing…
                                                    </span>
                                                ) : (
                                                    `Pay ₹${selectedPlan.plan_price}`
                                                )}
                                            </button>
                                        </div>
                                    </form>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            )}
            <AuthPopup
                isOpen={showAuth}
                onClose={() => setShowAuth(false)}
            />
            {role == 'open' && (<Footer />)}
        </div>
    );
}